/**
 * Объединение карт полей страницы (как в GlobalProvider.registerElement):
 * поздние источники дополняют ранние;
 */

import {
  fieldEntryLooksEmptyIncoming,
  shouldPreserveFieldEntryOnEmptyIncoming,
} from '@/common/utils/changedDataFieldMerge'

export const isTableLikeFieldValue = (field: any): boolean => {
  if (!field || typeof field !== 'object') return false
  const rawValue = 'value' in field ? field.value : field
  return Boolean(rawValue && typeof rawValue === 'object' && Array.isArray(rawValue.rows))
}

export type MergePageFieldMapsOptions = {
  /** true — не затирать непустое поле пустым incoming */
  shouldPreserveEmptyIncoming?: (fieldKey: string) => boolean
}

function isExplicitNullFieldEntry(entry: any): boolean {
  return Boolean(
    entry &&
      typeof entry === 'object' &&
      !Array.isArray(entry) &&
      'value' in entry &&
      (entry as { value?: unknown }).value === null,
  )
}

function tableLikeRowCount(field: any): number {
  if (!field || typeof field !== 'object') return 0
  const rows = field.rows ?? field.value?.rows
  return Array.isArray(rows) ? rows.length : 0
}

/**
 * Сервер явно пометил таблицу как пустую (`empty: true`)
 */
function isExplicitEmptyTableFieldValue(field: any): boolean {
  if (!field || typeof field !== 'object') return false
  const val = 'value' in field ? field.value : field
  return Boolean(val && typeof val === 'object' && (val as { empty?: unknown }).empty === true)
}

function isMergeOptions(value: unknown): value is MergePageFieldMapsOptions {
  return (
    value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'shouldPreserveEmptyIncoming' in value &&
    typeof (value as MergePageFieldMapsOptions).shouldPreserveEmptyIncoming === 'function'
  )
}

export function mergePageFieldMaps(
  ...sources: Array<Record<string, any> | null | undefined | MergePageFieldMapsOptions>
): Record<string, any> {
  let options: MergePageFieldMapsOptions | undefined
  const fieldSources: Array<Record<string, any> | null | undefined> = []

  for (const source of sources) {
    if (isMergeOptions(source)) {
      options = source
      continue
    }
    fieldSources.push(source)
  }

  const merged: Record<string, any> = {}

  for (const source of fieldSources) {
    if (!source || typeof source !== 'object') continue

    for (const [key, value] of Object.entries(source)) {
      if (!(key in merged)) {
        merged[key] = value
        continue
      }

      // Explicit null (setField("") + refill) clears earlier values, but a later
      // non-empty user/server value must be allowed to re-fill the field.
      if (isExplicitNullFieldEntry(merged[key])) {
        if (!isExplicitNullFieldEntry(value) && !fieldEntryLooksEmptyIncoming(value)) {
          merged[key] = value
        }
        continue
      }

      const isDocumentIdentityKey = key === '_id' || key === '_typeId' || key === '_versionId'

      // PK документа: поздний scalar (procedure replace) всегда побеждает table-bleed.
      if (isDocumentIdentityKey && !isTableLikeFieldValue(value)) {
        if (!shouldPreserveFieldEntryOnEmptyIncoming(merged[key], value)) {
          merged[key] = value
        }
        continue
      }

      // Пустая таблица из changed_data не должна блокировать нормальное значение из uniData
      if (isTableLikeFieldValue(merged[key]) && !isTableLikeFieldValue(value)) {
        if (tableLikeRowCount(merged[key]) === 0) {
          merged[key] = value
        }
        continue
      }

      // Сервер явно вернул `empty: true` в более раннем источнике (напр. changed_data
      // перед uniData в snapshot): не давать позднему stale uniData с rows вернуть строки.
      // Но если shouldPreserveEmptyIncoming(key)===false — поздний источник авторитетен
      // (reloadElement: empty→rows или rows→empty).
      if (
        isTableLikeFieldValue(merged[key]) &&
        isTableLikeFieldValue(value) &&
        tableLikeRowCount(merged[key]) === 0 &&
        isExplicitEmptyTableFieldValue(merged[key])
      ) {
        const allowAuthoritativeFill = options?.shouldPreserveEmptyIncoming?.(key) === false
        if (!allowAuthoritativeFill) {
          continue
        }
      }

      // Не затирать списки/таблицы пустыми rows при "мягком" слиянии.
      // Это защищает UI от отката данных назад, когда один из источников — snapshot/overlay,
      // а другой — более полный источник с уже загруженными строками.
      // Явный empty:true|false (после normalizeReload) — авторитетная замена, в т.ч. 1→0.

      if (
        isTableLikeFieldValue(merged[key]) &&
        isTableLikeFieldValue(value) &&
        tableLikeRowCount(merged[key]) > 0 &&
        tableLikeRowCount(value) === 0
      ) {
        const preserveTable =
          options?.shouldPreserveEmptyIncoming?.(key) ??
          !isExplicitEmptyTableFieldValue(value)
        if (preserveTable) {
          continue
        }
      }

      // Не затирать более длинную таблицу короткой в "мягком" merge
      // (например, dialogClose/overlay/snapshot). Для авторитетного reload/panel
      // используется отдельный путь apply с заменой полей из ответа.
      if (
        isTableLikeFieldValue(merged[key]) &&
        isTableLikeFieldValue(value) &&
        tableLikeRowCount(merged[key]) > 0 &&
        tableLikeRowCount(value) > 0 &&
        tableLikeRowCount(merged[key]) > tableLikeRowCount(value)
      ) {
        const preserveTable = options?.shouldPreserveEmptyIncoming?.(key) ?? true
        if (preserveTable) {
          continue
        }
      }

      if (shouldPreserveFieldEntryOnEmptyIncoming(merged[key], value)) {
        const preserve = options?.shouldPreserveEmptyIncoming?.(key) ?? true
        if (preserve) {
          continue
        }
      }

      merged[key] = value
    }
  }

  return merged
}

export function overlayAuthoritativeFieldKeys(
  merged: Record<string, any>,
  authoritative: Record<string, any> | null | undefined,
): Record<string, any> {
  if (!authoritative || typeof authoritative !== 'object') {
    return merged
  }
  const out = { ...merged }
  for (const key of Object.keys(authoritative)) {
    out[key] = authoritative[key]
  }
  return out
}
